import React from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
	Typography,
	TextField,
	Button,
	IconButton,
	Checkbox,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Chip,
	Grow
} from '@material-ui/core';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import ScheduleIcon from '@material-ui/icons/Schedule';
import clsx from 'clsx';
import { fileToDownscaledBase64 } from '../helpers';

const useStyles = makeStyles((theme) => ({
	head: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	dateField: {
		width: 170
	},
	addBtn: {
		marginLeft: 'auto',
		textTransform: 'none',
		fontWeight: 600
	},
	infoStrip: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(0.75, 2),
		fontSize: 12.5,
		color: theme.palette.text.secondary,
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.type === 'dark'
			? fade(theme.palette.common.white, 0.02)
			: fade(theme.palette.text.primary, 0.02)
	},
	infoIconGreen: {
		fontSize: 16,
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43'
	},
	infoIconYellow: {
		fontSize: 16,
		color: theme.palette.type === 'dark' ? '#e8dc00' : '#8a7d00'
	},
	headCell: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		padding: theme.spacing(0.75, 1)
	},
	cell: {
		padding: theme.spacing(0.75, 1),
		verticalAlign: 'top'
	},
	seq: {
		fontSize: 12,
		color: theme.palette.text.disabled,
		fontVariantNumeric: 'tabular-nums',
		paddingTop: theme.spacing(1.5)
	},
	descInput: {
		minWidth: 220
	},
	typeSelect: {
		width: 130
	},
	appliesSelect: {
		width: 160
	},
	skuChips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(0.75)
	},
	skuChip: {
		height: 22,
		fontSize: 10.5,
		fontFamily: 'monospace',
		cursor: 'pointer'
	},
	skuChipPicked: {
		backgroundColor: '#FEF200',
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		'&:hover': {
			backgroundColor: '#FFF53D'
		},
		'&:focus': {
			backgroundColor: '#FEF200'
		}
	},
	photoCell: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.75)
	},
	photoThumb: {
		width: 36,
		height: 36,
		objectFit: 'cover',
		borderRadius: 4,
		border: `1px solid ${theme.palette.divider}`
	},
	photoRemove: {
		fontSize: 11,
		color: theme.palette.error.main,
		cursor: 'pointer'
	},
	uploadLabel: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		fontSize: 12,
		color: theme.palette.text.secondary,
		cursor: 'pointer',
		padding: theme.spacing(0.5, 1),
		border: `1px dashed ${theme.palette.divider}`,
		borderRadius: 6,
		transition: theme.transitions.create(['border-color', 'color'], { duration: 150 }),
		'&:hover': {
			color: theme.palette.text.primary,
			borderColor: fade('#FEF200', 0.9)
		}
	},
	hiddenInput: {
		display: 'none'
	},
	uploadIcon: {
		fontSize: 14
	},
	smallIcon: {
		fontSize: 16
	},
	rowActions: {
		display: 'flex',
		alignItems: 'center'
	},
	iconBtn: {
		padding: 5,
		color: theme.palette.text.secondary,
		'&:hover': {
			color: theme.palette.text.primary
		}
	},
	deleteBtn: {
		'&:hover': {
			color: theme.palette.error.main,
			backgroundColor: fade(theme.palette.error.main, 0.12)
		}
	},
	empty: {
		padding: theme.spacing(2),
		color: theme.palette.text.disabled,
		fontStyle: 'italic',
		fontSize: 13
	},
	sectionError: {
		color: theme.palette.error.main,
		fontSize: 12.5,
		padding: theme.spacing(0, 2, 1.5)
	}
}));

const INPUT_TYPES = ['text', 'number', 'checkbox', 'photo'];

const newTask = () => ({
	id: '',
	description: '',
	input_type: 'checkbox',
	applies_to: 'all',
	ref_photo_url: '',
	ref_photo_base64: '',
	is_required: true,
	task_skus: []
});

export default function TaskSection({ form, onFormChange, language, errors }) {
	const { enqueueSnackbar } = useSnackbar();
	const classes = useStyles();

	const sectionErrors = (errors || []).filter((err) => err.section === 4 && !err.field);
	const fieldError = (field) => {
		const found = (errors || []).find((err) => err.field === field);
		return found ? found.message : '';
	};

	const typeLabels = {
		text: language.text || 'Text',
		number: language.number || 'Number',
		checkbox: language.checkbox || 'Checkbox',
		photo: language.photo || 'Photo'
	};

	const updateGlobal = (key) => (e) => {
		const value = e.target.value;
		onFormChange((prev) => ({ ...prev, [key]: value }));
	};

	const updateTask = (index, patch) => {
		onFormChange((prev) => ({
			...prev,
			tasks: prev.tasks.map((t, i) => (i === index ? { ...t, ...patch } : t))
		}));
	};

	const addTask = () => {
		onFormChange((prev) => ({ ...prev, tasks: [...prev.tasks, newTask()] }));
	};

	const removeTask = (index) => {
		onFormChange((prev) => ({ ...prev, tasks: prev.tasks.filter((t, i) => i !== index) }));
	};

	const moveTask = (index, dir) => {
		onFormChange((prev) => {
			const target = index + dir;
			if (target < 0 || target >= prev.tasks.length) {
				return prev;
			}
			const tasks = [...prev.tasks];
			const tmp = tasks[index];
			tasks[index] = tasks[target];
			tasks[target] = tmp;
			return { ...prev, tasks };
		});
	};

	const toggleTaskSku = (index, productId) => {
		onFormChange((prev) => ({
			...prev,
			tasks: prev.tasks.map((t, i) => {
				if (i !== index) {
					return t;
				}
				const key = String(productId);
				const picked = t.task_skus.map(String).includes(key);
				return {
					...t,
					task_skus: picked
						? t.task_skus.filter((pid) => String(pid) !== key)
						: [...t.task_skus, key]
				};
			})
		}));
	};

	const onPhotoPick = (index) => (e) => {
		const file = e.target.files && e.target.files[0];
		e.target.value = '';
		if (!file) {
			return;
		}
		fileToDownscaledBase64(file)
			.then((dataUrl) => updateTask(index, { ref_photo_base64: dataUrl, ref_photo_url: '' }))
			.catch(() => enqueueSnackbar(language.update_fail || 'Failed to process image', { variant: 'error' }));
	};

	const clearPhoto = (index) => () => {
		updateTask(index, { ref_photo_base64: '', ref_photo_url: '' });
	};

	return (
		<div>
			<div className={classes.head}>
				<TextField
					className={classes.dateField}
					variant="outlined"
					size="small"
					type="date"
					label={language.task_start_date}
					InputLabelProps={{ shrink: true }}
					value={form.task_start_date}
					error={!!fieldError('task_start_date')}
					helperText={fieldError('task_start_date')}
					onChange={updateGlobal('task_start_date')}
				/>
				<TextField
					className={classes.dateField}
					variant="outlined"
					size="small"
					type="date"
					label={language.task_due_date}
					InputLabelProps={{ shrink: true }}
					value={form.task_due_date}
					error={!!fieldError('task_due_date')}
					helperText={fieldError('task_due_date')}
					onChange={updateGlobal('task_due_date')}
				/>
				<Button
					className={classes.addBtn}
					variant="outlined"
					size="small"
					startIcon={<AddRoundedIcon />}
					onClick={addTask}>
					{language.add_task}
				</Button>
			</div>

			<div className={classes.infoStrip}>
				<NotificationsActiveOutlinedIcon className={classes.infoIconGreen} />
				<span>{language.task_notify_hint}</span>
			</div>
			<div className={classes.infoStrip}>
				<ScheduleIcon className={classes.infoIconYellow} />
				<span>{language.task_due_hint}</span>
			</div>

			{form.tasks.length === 0 ? (
				<Typography className={classes.empty}>{language.no_data || 'No tasks'}</Typography>
			) : (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell className={classes.headCell}>#</TableCell>
							<TableCell className={classes.headCell}>{language.task_description}</TableCell>
							<TableCell className={classes.headCell}>{language.input_type}</TableCell>
							<TableCell className={classes.headCell}>{language.applies_to}</TableCell>
							<TableCell className={classes.headCell}>{language.reference_photo}</TableCell>
							<TableCell className={classes.headCell}>{language.required}</TableCell>
							<TableCell className={classes.headCell} />
						</TableRow>
					</TableHead>
					<TableBody>
						{form.tasks.map((task, index) => (
							<Grow in key={task.id || `new-${index}`} timeout={240}>
								<TableRow>
									<TableCell className={classes.cell}>
										<Typography className={classes.seq}>{index + 1}</Typography>
									</TableCell>
									<TableCell className={classes.cell}>
										<TextField
											fullWidth
											className={classes.descInput}
											variant="outlined"
											size="small"
											placeholder={language.task_description}
											value={task.description}
											onChange={(e) => updateTask(index, { description: e.target.value })}
										/>
									</TableCell>
									<TableCell className={classes.cell}>
										<TextField
											select
											className={classes.typeSelect}
											variant="outlined"
											size="small"
											value={task.input_type}
											onChange={(e) => updateTask(index, { input_type: e.target.value })}>
											{INPUT_TYPES.map((t) => (
												<MenuItem key={t} value={t}>{typeLabels[t]}</MenuItem>
											))}
										</TextField>
									</TableCell>
									<TableCell className={classes.cell}>
										<TextField
											select
											className={classes.appliesSelect}
											variant="outlined"
											size="small"
											value={task.applies_to}
											onChange={(e) => updateTask(index, { applies_to: e.target.value, task_skus: [] })}>
											<MenuItem value="all">{`${language.all_skus} (${form.skus.length})`}</MenuItem>
											<MenuItem value="specific">{language.specific_skus}</MenuItem>
										</TextField>
										{task.applies_to === 'specific' && (
											<div className={classes.skuChips}>
												{form.skus.length === 0 ? (
													<Typography variant="caption" color="textSecondary">
														{language.err_min_one_sku}
													</Typography>
												) : form.skus.map((sku) => {
													const picked = task.task_skus.map(String).includes(String(sku.product_id));
													return (
														<Chip
															key={sku.product_id}
															size="small"
															label={sku.product_code}
															className={clsx(classes.skuChip, picked && classes.skuChipPicked)}
															onClick={() => toggleTaskSku(index, sku.product_id)}
														/>
													);
												})}
											</div>
										)}
									</TableCell>
									<TableCell className={classes.cell}>
										{(task.ref_photo_base64 || task.ref_photo_url) ? (
											<div className={classes.photoCell}>
												<img
													className={classes.photoThumb}
													src={task.ref_photo_base64 || task.ref_photo_url}
													alt={language.reference_photo}
												/>
												<span
													className={classes.photoRemove}
													onClick={clearPhoto(index)}
													role="button"
													tabIndex={0}
													onKeyPress={clearPhoto(index)}>
													{language.delete}
												</span>
											</div>
										) : (
											<label className={classes.uploadLabel}>
												<input
													className={classes.hiddenInput}
													type="file"
													accept="image/*"
													onChange={onPhotoPick(index)}
												/>
												<PublishOutlinedIcon className={classes.uploadIcon} />
												{language.upload || 'Upload'}
											</label>
										)}
									</TableCell>
									<TableCell className={classes.cell} align="center">
										<Checkbox
											color="primary"
											size="small"
											checked={task.is_required}
											onChange={(e) => updateTask(index, { is_required: e.target.checked })}
										/>
									</TableCell>
									<TableCell className={classes.cell}>
										<div className={classes.rowActions}>
											<IconButton className={classes.iconBtn} disabled={index === 0} onClick={() => moveTask(index, -1)}>
												<ArrowUpwardIcon className={classes.smallIcon} />
											</IconButton>
											<IconButton className={classes.iconBtn} disabled={index === form.tasks.length - 1} onClick={() => moveTask(index, 1)}>
												<ArrowDownwardIcon className={classes.smallIcon} />
											</IconButton>
											<IconButton className={clsx(classes.iconBtn, classes.deleteBtn)} onClick={() => removeTask(index)}>
												<DeleteOutlineIcon className={classes.smallIcon} />
											</IconButton>
										</div>
									</TableCell>
								</TableRow>
							</Grow>
						))}
					</TableBody>
				</Table>
			)}
			{sectionErrors.map((err) => (
				<Typography key={err.message} className={classes.sectionError}>{err.message}</Typography>
			))}
		</div>
	);
}
